import { defineStore, acceptHMRUpdate } from 'pinia';
import { useThemesStore } from '@/stores/themes.store';
import { LOADING_STATES } from '@/constants';
import { i18n_t as $t } from '@/utils/i18n';
import {
    BRANCHING_NODES_TYPES,
    ENGAGEMENT_GOAL_TYPES,
    ERROR_MESSAGES,
    MESSAGE_DROPDOWN_OPTIONS,
    MESSAGE_TYPES,
} from '@/orchestrate/journeys/constants';
import {
    buildAudienceAndAudienceUiHint,
    buildJourney,
    cloneMessageGraph,
    extractMessagesFromMessageGraph,
    getDisplayMessageGraph,
    getAllGuidesIdsByJourney,
    formatMessageDropdownOption,
    getInsertionChainDepth,
    messagesGenerator,
    getMessageSidebladeConfig,
    sanitizeMessageGraph,
    shouldAddOptionToMessageDropdown,
} from '@/orchestrate/journeys/helpers';
import {
    createJourney as createJourneyApi,
    deleteJourney as deleteJourneyApi,
    getAllJourneys,
    getJourneyById as getJourneyByIdApi,
    updateJourney as updateJourneyApi,
    updateJourneyAudience as updateJourneyAudienceApi,
    updateJourneyStatus as updateJourneyStatusApi,
    updateMessageGraph as updateMessageGraphApi,
    removeOrphanGraphElements,
    updateSchedule as updateScheduleApi,
} from '@/vuex/modules/journeys/journeys.utils';
import { getClonedEmailDisplayName, isHtmlEmail, isIntegratedEmail } from '@/emails/helpers';
import { EMAIL_CONTENT_SOURCE } from '@/emails/constants';
import { useEmailsStore } from '@/stores/emails.store';
import { createGuideFromLayout } from '@/guides/helpers/common.util';
import { CHANNEL_TYPES, PROVIDER_TYPES } from '@/orchestrate/journeys/integrations.constants';
import { STATUS_TYPES } from '@/orchestrate/journeys/status.constants';
import { loadEmailsByProvider } from '@/emails/emails.utils';
import { getEmailType, isEmailTransactional } from '@pendo/services/EmailUtils';
import { getJourneyMessagesForJourneyLayout } from '@pendo/services/Journey';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import {
    initializeNodeIdMapping,
    processRegularNodes,
    setStartNodeIds,
    updateConditionNodes,
} from '@/vuex/modules/journeys/journeys.helpers';
import { canCreateJourney, canPublishJourney, canUpdateJourney } from '@/vuex/modules/journeys/journeys-permissions.utils';
import { useGuidesStore } from '@/stores/guides.store';

import { useProductLinesStore } from '@/stores/product-lines.store';
import { useSubscriptionStore } from '@/stores/subscription.store';

import { useAuthStore } from '@/stores/auth.store';

const { LOADING, RESOLVED, REJECTED, EMPTY } = LOADING_STATES;
const { GUIDE, EMAIL } = MESSAGE_TYPES;
const { AWS, HUBSPOT } = PROVIDER_TYPES;
const { PAUSED, DISABLED, ACTIVE, COMPLETED } = STATUS_TYPES;

let loadAllPromise: Promise<void> | null = null;

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}

export interface Journey {
    id: string;
    name?: string;
    appId?: string;
    status?: string;
    goal?: Record<string, unknown>;
    isGoalOptional?: boolean;
    audience?: unknown[];
    audienceUiHint?: unknown;
    messageGraph?: {
        startNodeId?: string;
        nodes: Array<{ id: string; nodeType: string; message?: { id: string; type: string; provider?: string; guideId?: string }; messageData?: Record<string, unknown>; [key: string]: unknown }>;
        edges: Array<{ id: string; from: string; to: string; edgeType?: string; [key: string]: unknown }>;
    };
    emailSettings?: Record<string, unknown>;
    schedulingSettings?: { startDate?: string };
    [key: string]: unknown;
}

export interface MessageSidebladeConfig {
    contentVariant?: string;
    showSettingsTab?: boolean;
    showThrottlingSection?: boolean;
    usesGuideLocalization?: boolean;
    usesEmailLocalization?: boolean;
    durationUsesGuideLabel?: boolean;
    requiresGuideForPreview?: boolean;
    byContentSource?: Record<string, Pick<MessageSidebladeConfig, 'contentVariant' | 'requiresGuideForPreview'>>;
}

interface JourneysState {
    map: Record<string, Journey>;
    currentJourneyId: string | null;
    currentMessageId: string | null;
    getAllJourneysStatus: string;
    editing: boolean;
    saving: boolean;
    error: unknown;
    journeySegmentName: string;
    journeySegmentVisitorCount: number;
    isCloningJourney: boolean;
    loadingMessages: boolean;
    integrationsByProvider: Record<string, Record<string, unknown[]>>;
    integrationError: Record<string, unknown>;
    journeyAnalyticsAggData: { status?: string; result?: unknown };
}

export function getInitialState (): JourneysState {
    return {
        map: {},
        currentJourneyId: null,
        currentMessageId: null,
        getAllJourneysStatus: LOADING,
        editing: false,
        saving: false,
        error: null,
        journeySegmentName: '',
        journeySegmentVisitorCount: 0,
        isCloningJourney: false,
        loadingMessages: false,
        integrationsByProvider: {},
        integrationError: {},
        journeyAnalyticsAggData: {},
    };
}

export const useJourneysStore = defineStore('journeys', {
    state: getInitialState,
    getters: {
        // NOTE: getSaving / getError / getMap / getJourneySegmentVisitorCount dropped (circular getter names).
        // Consumers should access store.saving, store.error, store.map, store.journeySegmentVisitorCount directly.
        getIntegrationError: (state) => (provider: string) => state.integrationError[provider],
        list: (state): Journey[] => Object.values(state.map),
        getJourneyById: (state) => (id: string): Journey | undefined => state.map[id],
        getActiveJourney (state): Journey | undefined {
            return state.map[state.currentJourneyId!];
        },
        displayMessageGraph (state): Journey['messageGraph'] | undefined {
            const graph = state.map[state.currentJourneyId!]?.messageGraph;

            if (!graph) {
                return graph;
            }

            return getDisplayMessageGraph(graph) ?? graph;
        },
        getActiveMessage (state) {
            const store = useJourneysStore();
            const messages: unknown[] = store.getMessages;

            return (messages as Array<{ id: string }>).find((message) => message.id === state.currentMessageId);
        },
        getActiveMessageType (): string | undefined {
            const store = useJourneysStore();
            const message = store.getActiveMessage as { type?: string } | undefined;

            return message?.type;
        },
        getActiveEmailContentSource (): string | undefined {
            const store = useJourneysStore();
            const message = store.getActiveMessage as { id?: string; type?: string } | undefined;

            if (message?.type !== EMAIL) return undefined;

            const email = useEmailsStore().getEmailById(message.id as string);

            return email?.contentSource ?? EMAIL_CONTENT_SOURCE.BLOCKS;
        },
        getActiveMessageSidebladeConfig (): MessageSidebladeConfig | null {
            const store = useJourneysStore();

            return getMessageSidebladeConfig(
                store.getActiveMessageType,
                store.getActiveEmailContentSource,
            ) as MessageSidebladeConfig | null;
        },
        getEmailSettings (): unknown {
            const store = useJourneysStore();

            return store.getActiveJourney?.emailSettings;
        },
        isEmailSettingsEmpty (): boolean {
            const store = useJourneysStore();
            const emailSettings = store.getEmailSettings as Record<string, unknown> | undefined;

            return !emailSettings || !emailSettings.identity;
        },
        getGoal (): Record<string, unknown> {
            const store = useJourneysStore();
            const activeJourney = store.getActiveJourney;

            return activeJourney?.isGoalOptional
                ? { itemType: ENGAGEMENT_GOAL_TYPES.NO_GOAL }
                : (activeJourney?.goal as Record<string, unknown>) || {};
        },
        getIsGoalOptional (): boolean {
            const store = useJourneysStore();

            return store.getActiveJourney?.isGoalOptional || false;
        },
        getJourneyStatus (): string | undefined {
            const store = useJourneysStore();

            return store.getActiveJourney?.status;
        },
        isLocalizationEnabled (): (appId: string) => boolean {
            return (appId: string) => {
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const { useAppsStore } = require('@/stores/apps.store');
                const app = useAppsStore().getAppById(appId);
                const localizationEnabledForApp = get(app, 'applicationFlags.guideLocalization', false);
                const hasPurchasedGuidesLocalization = useProductLinesStore().hasProductLinePurchased(
                    'guides',
                    'localizationEnabled',
                );

                return localizationEnabledForApp && hasPurchasedGuidesLocalization;
            };
        },
        getPromotionById (): (id: string) => Record<string, unknown> {
            return (id: string) => {
                const guide = useGuidesStore().getGuideById(id);

                if (!guide) return {};

                return {
                    ...guide,
                    guideId: (guide as Record<string, unknown>).id,
                };
            };
        },
        getMessages (): unknown[] {
            const store = useJourneysStore();
            const activeJourney = store.getActiveJourney;
            const { getEmailById } = useEmailsStore();
            const messages = store.displayMessageGraph
                ? extractMessagesFromMessageGraph(store.displayMessageGraph)
                : [];

            return messages.map((message: { id: string; type: string; appId?: string; provider?: string }) => {
                const entity = message.type === EMAIL
                    ? getEmailById?.(message.id)
                    : store.getPromotionById(message.id);
                const { guideId, name, description, emailType, ignoreThrottling } = (entity as Record<string, unknown>) || {};
                const returnObject: Record<string, unknown> = {
                    ...message,
                    name,
                    guideId,
                    appId: message.appId || activeJourney?.appId,
                    description,
                };

                if (message.type === EMAIL) {
                    returnObject.emailType = emailType;
                    returnObject.ignoreThrottling = ignoreThrottling;
                    if ((entity as Record<string, unknown>)?.provider === AWS) {
                        returnObject.isEmptyEmailSettings = !((entity as Record<string, unknown>)?.emailSettings as Record<string, unknown>)?.identity;
                    } else {
                        returnObject.isEmptyEmailSettings = false;
                    }
                    returnObject.isEmptyHtmlContent = isHtmlEmail(entity as Record<string, unknown>)
                        && !(entity as Record<string, unknown>)?.htmlTemplateUrl;
                }

                return returnObject;
            });
        },
        getConditions (): unknown[] {
            const store = useJourneysStore();
            const displayGraph = store.displayMessageGraph;

            if (!displayGraph) return [];

            const conditionNodes = displayGraph.nodes.filter(
                (node: { nodeType: string; id: string }) => node.nodeType === BRANCHING_NODES_TYPES.CONDITION,
            );

            return Array.from(
                new Map(conditionNodes.map((node: { nodeType: string; id: string }) => [node.id, node])).values(),
            );
        },
        getInsertionChainDepth (): (sourceId: string, targetId: string) => number {
            const store = useJourneysStore();

            return (sourceId: string, targetId: string) => {
                const displayGraph = store.displayMessageGraph;

                if (!displayGraph) return 0;

                return getInsertionChainDepth(sourceId, targetId, displayGraph.nodes, displayGraph.edges);
            };
        },
        getMessageById (): (id: string) => unknown {
            const store = useJourneysStore();

            return (id: string) => (store.getMessages as Array<{ id: string }>).find((message) => message.id === id);
        },
        categorizedMessagesMenuOptions (): unknown[] {
            // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
            const vuexStore = require('@/vuex/store').default; // TODO: replace when emails/guides/apps migrate
            const rootGetters = vuexStore?.getters;

            return (
                MESSAGE_DROPDOWN_OPTIONS
                    .filter((category: unknown) => shouldAddOptionToMessageDropdown(rootGetters, category))
                    .map((category: Record<string, unknown>, index: number) => ({
                        ...category,
                        ...(index > 0 ? { showDivider: true } : {}),
                    }))
                    .flatMap((category: Record<string, unknown>) => [category, ...(category.items as unknown[] || [])])
                    .filter((option: unknown) => (option as Record<string, unknown>).category || shouldAddOptionToMessageDropdown(rootGetters, option))
                    .map((option: unknown) => {
                        const opt = option as Record<string, unknown>;

                        if (opt.category) {
                            return {
                                category: opt.category,
                                showDivider: opt.showDivider,
                            };
                        }

                        return formatMessageDropdownOption(rootGetters, opt);
                    })
            );
        },
        hasJourneysEnabled (): boolean {
            return useProductLinesStore().hasProductLinePurchased('orchestrate');
        },
        getIntegrationsByProvider: (state) => (provider: string, channel: string): unknown[] => {
            return state.integrationsByProvider[provider]?.[channel] || [];
        },
        getJourneyAnalyticsAggData: (state) => state.journeyAnalyticsAggData.result,
        getJourneyAnalyticsAggStatus: (state) => state.journeyAnalyticsAggData.status,
        isJourneyLaunched (): boolean {
            const store = useJourneysStore();
            const launchedStatuses = [ACTIVE, COMPLETED, DISABLED, PAUSED];

            return launchedStatuses.includes(store.getActiveJourney?.status as string);
        },
        isBlockedByStatus (): boolean {
            const store = useJourneysStore();
            const blocksEditingStatuses = [ACTIVE, COMPLETED, DISABLED, PAUSED];

            return blocksEditingStatuses.includes(store.getJourneyStatus as string);
        },
        isEditableWhenPaused (): boolean {
            const store = useJourneysStore();

            return store.getJourneyStatus === PAUSED;
        },
        isNotAnEmptyJourney: () => (journey: Journey): boolean => {
            return journey.messageGraph!.edges.length > 1 || journey.messageGraph!.nodes.length > 2;
        },
        canEditAllJourneyMessages (): boolean {
            const store = useJourneysStore();

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (store.getMessages as Array<{ appId: string }>).every(({ appId }) => canUpdateJourney({ appId } as any));
        },
        canCloneAllJourneyMessages (): boolean {
            const store = useJourneysStore();

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (store.getMessages as Array<{ appId: string }>).every(({ appId }) => canCreateJourney({ appId } as any));
        },
        canPublishAllJourneyMessages (): boolean {
            const store = useJourneysStore();

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (store.getMessages as Array<{ appId: string }>).every(({ appId }) => canPublishJourney({ appId } as any));
        },
        getJourneyRoute (): ({ journeyId, messageId }: { journeyId: string; messageId?: string }) => Record<string, unknown> {
            const store = useJourneysStore();

            return ({ journeyId, messageId }: { journeyId: string; messageId?: string }) => {
                if (store.getJourneyById(journeyId)) {
                    return {
                        name: 'JourneyDetails',
                        params: { id: journeyId },
                        query: { view: 'map', messageId },
                    };
                }

                return { name: 'journeyForbidden', query: { messageId } };
            };
        },
        hasJourneyThrottlingPermission (): boolean {
            const isJourneyThrottleSettingsEnabled = useSubscriptionStore().getSubscriptionJourneyThrottleSettings?.enabled;
            const { hasSegmentFlag } = useAuthStore();

            return Boolean(isJourneyThrottleSettingsEnabled) && hasSegmentFlag('journeyThrottling');
        },
        getJourneysTabs (): Array<{ prop: string; label: string; lazy: boolean }> {
            const { hasSegmentFlag } = useAuthStore();
            const tabs: Array<{ prop: string; label: string; lazy: boolean }> = [
                { prop: 'overview', label: $t('journeys.Overview'), lazy: true },
            ];

            if (hasSegmentFlag?.('journeyThrottling')) {
                tabs.push({ prop: 'settings', label: $t('journeys.Settings'), lazy: true });
            }

            return tabs;
        },
    },
    actions: {
        async buildAndCreateJourney (
            { appId, name, target = null, segment = null, description = '' }: {
                appId: string;
                name: string;
                target?: unknown;
                segment?: unknown;
                description?: string;
            },
        ) {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useAppsStore } = require('@/stores/apps.store');
            const app = useAppsStore().getAppById(appId);

            let audience: unknown[] = [
                { source: { visitors: null } },
                { filter: 'false' },
            ];
            let audienceUiHint: unknown = {
                filters: [{ kind: 'visitor', segmentId: 'noOne' }],
            };

            if (segment) {
                ({ audience, audienceUiHint } = await buildAudienceAndAudienceUiHint(segment, app));
            }

            const journey = await buildJourney({
                audience,
                audienceUiHint,
                app,
                name,
                description,
                target,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any);

            return this.createJourney({ journey });
        },
        async cloneJourney ({ journey, app, preventCodeBlocks }: { journey: Journey; app: unknown; preventCodeBlocks: boolean }) {
            this.isCloningJourney = true;

            let createdJourney: Journey | undefined;
            try {
                if (!journey.messageGraph || !journey.messageGraph.nodes) {
                    throw new Error('Cannot clone journey without a messageGraph');
                }

                const displayGraph = getDisplayMessageGraph(journey.messageGraph);

                const newJourney = await buildJourney({
                    audience: journey.audience,
                    audienceUiHint: journey.audienceUiHint,
                    app,
                    name: `Copy of ${journey.name}`,
                    target: journey.goal,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any);

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (newJourney as any).isGoalOptional = this.getIsGoalOptional;

                createdJourney = await this.createJourney({ journey: newJourney });
                const originalMessages = extractMessagesFromMessageGraph(displayGraph);
                const clonedMessages = await this.cloneMessagesFromMessageGraph({
                    originalMessages,
                    preventCodeBlocks,
                    fallbackAppId: journey.appId,
                });

                const emailCreationPromises: Promise<unknown>[] = [];
                const emailIdMapping: Record<string, string> = {};
                const emailsStore = useEmailsStore();

                for (let i = 0; i < originalMessages.length; i++) {
                    const originalMessage = originalMessages[i] as Record<string, unknown>;
                    const clonedMessage = (clonedMessages as Array<Record<string, unknown>>)[i];

                    if (originalMessage.type === EMAIL) {
                        const sourceEmail = emailsStore.getEmailById(originalMessage.id as string);
                        const clonedName = getClonedEmailDisplayName(sourceEmail?.name as string);

                        const emailPromise = (isHtmlEmail(sourceEmail)
                            ? emailsStore.cloneOmnichannel({
                                id: originalMessage.id as string,
                                name: clonedName,
                                journeyId: createdJourney!.id,
                            })
                            : emailsStore.createOmnichannel({
                                appId: clonedMessage.appId as string,
                                attributes: { journeyId: createdJourney!.id },
                                channelType: 'email',
                                guideId: clonedMessage.id as string,
                                name: clonedName,
                                provider: (originalMessage.provider as string) || AWS,
                                schedule: { sendPeriodically: true },
                                emailType: getEmailType(clonedMessage),
                            })
                        ).then((email: Record<string, string>) => {
                            emailIdMapping[originalMessage.id as string] = email.id;

                            return email;
                        });
                        emailCreationPromises.push(emailPromise);
                    }
                }

                await Promise.all(emailCreationPromises);

                const messageIdMapping: Record<string, string> = {};
                for (let i = 0; i < originalMessages.length; i++) {
                    const originalMessage = originalMessages[i] as Record<string, unknown>;
                    const clonedMessage = (clonedMessages as Array<Record<string, unknown>>)[i];

                    if (originalMessage.type === EMAIL) {
                        messageIdMapping[originalMessage.id as string] = emailIdMapping[originalMessage.id as string];
                    } else {
                        messageIdMapping[originalMessage.id as string] = clonedMessage.id as string;
                    }
                }

                const clonedMessageGraph = cloneMessageGraph(displayGraph, messageIdMapping);

                clonedMessageGraph.nodes.forEach((node: Record<string, unknown>) => {
                    if (
                        node.nodeType === BRANCHING_NODES_TYPES.MESSAGE &&
                        node.message &&
                        (node.message as Record<string, unknown>).type === EMAIL &&
                        !isHtmlEmail(emailsStore.getEmailById((node.message as Record<string, unknown>).id as string))
                    ) {
                        const originalMessageIndex = originalMessages.findIndex(
                            (message: Record<string, unknown>) =>
                                message.id ===
                                Object.keys(messageIdMapping).find(
                                    (key) => messageIdMapping[key] === (node.message as Record<string, unknown>).id,
                                ),
                        );
                        if (originalMessageIndex !== -1) {
                            const clonedGuide = (clonedMessages as Array<Record<string, unknown>>)[originalMessageIndex];
                            (node.message as Record<string, unknown>).guideId = clonedGuide.id;
                        }
                    }
                });

                await this.updateMessageGraph({
                    journeyId: createdJourney!.id,
                    messageGraph: clonedMessageGraph,
                });

                const clonedGuides = originalMessages
                    .map((msg: Record<string, unknown>, i: number) => (msg.type === GUIDE ? (clonedMessages as Array<Record<string, unknown>>)[i] : null))
                    .filter(Boolean);
                await this.linkClonedGuidesToJourney({ clonedGuides, journeyId: createdJourney!.id });

                await this.loadOne({ id: createdJourney!.id, reFetch: true });
            } finally {
                this.isCloningJourney = false;
            }

            return createdJourney;
        },
        async createJourney ({ journey }: { journey: unknown }) {
            try {
                const createdJourney = await createJourneyApi(journey);
                this.map[createdJourney.id] = createdJourney;

                return createdJourney;
            } catch (ex) {
                throw new Error(`create journey unsuccessful: ${ex}`);
            }
        },
        async linkClonedGuidesToJourney ({ clonedGuides, journeyId }: { clonedGuides: unknown[]; journeyId: string }) {
            if (clonedGuides.length === 0) return;

            const results = await Promise.allSettled(
                clonedGuides.map((clonedGuide) =>
                    useGuidesStore().patchGuide({
                        guide: {
                            ...(clonedGuide as Record<string, unknown>),
                            attributes: {
                                ...((clonedGuide as Record<string, unknown>).attributes || {}),
                                journeyId,
                            },
                        },
                        remoteGuide: clonedGuide,
                        fieldsToPatch: ['attributes'],
                    }),
                ),
            );

            const rejected = results
                .map((result, i) => (result.status === 'rejected' ? { guide: clonedGuides[i] } : null))
                .filter(Boolean);

            if (rejected.length > 0) {
                const rejectedNames = (rejected as Array<{ guide: Record<string, unknown> }>)
                    .map(({ guide }) => (guide.name || guide.id) as string)
                    .join(', ');
                const err = new Error(
                    `${rejected.length} of ${clonedGuides.length} guide(s) could not be linked:\n` +
                    `${rejectedNames}\n`,
                ) as Error & { createdJourneyId: string };
                err.createdJourneyId = journeyId;
                throw err;
            }
        },
        async loadAllJourneys ({ reFetch = false }: { reFetch?: boolean } = {}) {
            const hasOrchestrateProductLine = useProductLinesStore().hasProductLinePurchased('orchestrate');
            if (!hasOrchestrateProductLine) return;

            if (loadAllPromise && !reFetch) {
                await loadAllPromise;

                return;
            }

            this.map = {};
            this.getAllJourneysStatus = LOADING;
            loadAllPromise = (async () => {
                try {
                    const journeys = await getAllJourneys();
                    const journeysMap = (journeys as Journey[]).reduce((map: Record<string, Journey>, journey: Journey) => {
                        map[journey.id] = journey;

                        return map;
                    }, {});
                    this.getAllJourneysStatus = journeys.length ? RESOLVED : EMPTY;
                    this.map = journeysMap;
                } catch (err) {
                    console.error(err);
                    this.getAllJourneysStatus = REJECTED;
                }
            })();

            await loadAllPromise;
        },
        async loadOne ({ reFetch = false, id }: { reFetch?: boolean; id: string } = { id: '' }) {
            this.error = null;
            if (this.map[id] && !reFetch) {
                this.setActiveJourney({ id });

                return;
            }

            try {
                const journey = await getJourneyByIdApi(id);
                this.map[journey.id] = journey;
                this.setActiveJourney({ id });
            } catch (error) {
                this.error = error;
                throw error;
            }
        },
        async updateJourney ({ journey, reFetch = true }: { journey: Journey; reFetch?: boolean }) {
            this.error = null;
            this.saving = true;

            try {
                const updatedJourney = await updateJourneyApi(journey);
                this.map[updatedJourney.id] = updatedJourney;

                if (reFetch) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    getAllGuidesIdsByJourney(journey as any).forEach((id: string) =>
                        useGuidesStore().loadOne({ id, reFetch }),
                    );
                }
            } catch (err) {
                console.error(err);
                this.error = ERROR_MESSAGES.JOURNEY_UPDATE;
                throw err;
            } finally {
                this.saving = false;
            }
        },
        async updateJourneyAudience ({ id, audience, audienceUiHint, reachInactiveVisitors, retainVisitorsAfterEntry }: {
            id: string;
            audience: unknown;
            audienceUiHint: unknown;
            reachInactiveVisitors: unknown;
            retainVisitorsAfterEntry: unknown;
        }) {
            this.error = null;
            this.saving = true;

            try {
                const updatedJourney = await updateJourneyAudienceApi(id, audience, audienceUiHint, reachInactiveVisitors as boolean | undefined, retainVisitorsAfterEntry as boolean | undefined);
                this.map[updatedJourney.id] = updatedJourney;
            } catch (err) {
                console.error(err);
                this.error = ERROR_MESSAGES.JOURNEY_UPDATE;
            }

            this.saving = false;
        },
        async updateSchedule ({ id, scheduleData }: { id: string; scheduleData: unknown }) {
            this.error = null;
            this.saving = true;

            try {
                const updatedJourney = await updateScheduleApi(id, scheduleData);
                this.map[updatedJourney.id] = updatedJourney;
            } catch (err) {
                console.error(err);
                this.error = ERROR_MESSAGES.JOURNEY_UPDATE;
            }

            this.saving = false;
        },
        async deleteJourney ({ id }: { id: string }) {
            const messagesToRemovePromises: Promise<unknown>[] = [];
            const journey = this.map[id];

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            for (const message of messagesGenerator(journey as any)) {
                if ((message as Record<string, unknown>).type === GUIDE) {
                    messagesToRemovePromises.push(useGuidesStore().remove({ guide: message }));
                }

                if ((message as Record<string, unknown>).type === EMAIL) {
                    messagesToRemovePromises.push(useEmailsStore().deleteEmail({ id: (message as Record<string, unknown>).id as string }));
                }
            }

            try {
                await Promise.all(messagesToRemovePromises);
                await deleteJourneyApi(id);
                delete this.map[id];
                this.resetActiveJourney();
            } catch (ex) {
                throw new Error(`delete journey unsuccessful: ${ex}`);
            }
        },
        setActiveJourney ({ id }: { id: string }) {
            this.error = null;
            const journey = this.map[id];

            if (!journey) {
                this.error = ERROR_MESSAGES.JOURNEY_NOT_FOUND;

                return;
            }

            this.resetActiveJourney();
            this.currentJourneyId = id;
        },
        resetActiveJourney () {
            this.currentJourneyId = null;
            this.journeySegmentVisitorCount = 0;
            this.currentMessageId = null;
        },
        setActiveMessage ({ messageId }: { messageId: string | null }) {
            this.currentMessageId = messageId;

            const activeMsg = this.getActiveMessage as Record<string, unknown> | undefined;
            const type = activeMsg?.type;
            const guideId = activeMsg?.guideId;

            if (type === EMAIL || type === HUBSPOT || !messageId) {
                useEmailsStore().setActiveEmail({ id: messageId as string, guideId: guideId as string });
            }

            if (type === GUIDE || !messageId) {
                useGuidesStore().setActiveGuideById({ id: guideId as string });
            }
        },
        async cloneMessagesFromMessageGraph ({ originalMessages, preventCodeBlocks, fallbackAppId }: {
            originalMessages: unknown[];
            preventCodeBlocks: boolean;
            fallbackAppId?: string;
        }) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const hasIntegratedEmails = (originalMessages as Array<Record<string, unknown>>).some((message) => isIntegratedEmail(message as any));
            if (hasIntegratedEmails) {
                throw new Error('Cannot clone journey containing integrated emails');
            }

            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useAppsStore } = require('@/stores/apps.store');
            const promises = (originalMessages as Array<Record<string, unknown>>).map((message) => {
                if (message.type === EMAIL) {
                    const email = useEmailsStore().getEmailById(message.id as string);

                    if (isHtmlEmail(email)) {
                        return {
                            id: message.id,
                            name: email?.name,
                            description: email?.description,
                            appId: email?.appId ?? fallbackAppId,
                        };
                    }

                    return (async () => {
                        const clonedGuide = await useEmailsStore().cloneGuideByEmailId({ id: message.id as string });

                        return {
                            ...clonedGuide,
                            description: email?.description,
                            guideId: (clonedGuide as Record<string, unknown>)?.id,
                        };
                    })();
                }

                const { id } = message;
                const appId = (message.appId as string) || fallbackAppId;
                const guide = useGuidesStore().getGuideById(id as string);
                const app = useAppsStore().getAppById(appId);

                return useGuidesStore().cloneGuide({
                    guide,
                    app,
                    options: { cloneCodeBlock: !preventCodeBlocks, isCloningIntoJourney: true },
                });
            });

            return Promise.all(promises);
        },
        async createJourneyTemplate ({ selectedLayout, journeyId, app, theme }: {
            selectedLayout: Record<string, unknown>;
            journeyId: string;
            app: unknown;
            theme: unknown;
        }) {
            this.error = null;

            try {
                if (selectedLayout.messageGraph) {
                    await this.createJourneyFromMessageGraph({ selectedLayout, journeyId, app, theme });
                }
            } catch (err) {
                this.error =
                    (err as Error).message === 'Cannot clone journey containing integrated emails'
                        ? (err as Error).message
                        : ERROR_MESSAGES.MISSING_MESSAGE;
            }
        },
        async createJourneyFromMessageGraph ({ selectedLayout, journeyId, app, theme }: {
            selectedLayout: Record<string, unknown>;
            journeyId: string;
            app: unknown;
            theme: unknown;
        }) {
            const journey = this.map[journeyId];
            this.setActiveJourney({ id: journeyId });

            const isNotAnEmptyJourney = this.isNotAnEmptyJourney(journey);
            if (isNotAnEmptyJourney) return;

            if (selectedLayout.messageGraph) {
                selectedLayout.messageGraph = sanitizeMessageGraph(selectedLayout.messageGraph);
            }

            await this.cleanJourney({ journey });

            const nodeIdMap = initializeNodeIdMapping(selectedLayout, journey.messageGraph!.startNodeId);
            setStartNodeIds(selectedLayout, journey.messageGraph!.startNodeId);

            const { emailNodes, regularNodes } = await this.createMessagesForMessageNodes({
                selectedLayout,
                journeyId,
                app,
                theme,
                nodeIdMap,
            });

            processRegularNodes(regularNodes);

            await this.processEmailNodes({ emailNodes, journeyId, app, theme });

            updateConditionNodes(selectedLayout, regularNodes, emailNodes);

            await this.createGraphNodesAndEdges({ selectedLayout, journey, nodeIdMap });
        },
        async cleanJourney ({ journey }: { journey: Journey }) {
            const { useJourneysGraphStore } = await import('@/stores/journeys-graph.store');
            const graphStore = useJourneysGraphStore();
            await graphStore.deleteEdge({
                journeyId: journey.id,
                edgeId: journey.messageGraph!.edges[0].id,
            });
            await graphStore.deleteNode({
                journeyId: journey.id,
                nodeId: journey.messageGraph!.nodes[1].id,
            });
        },
        async createMessagesForMessageNodes ({ selectedLayout, app, theme }: {
            selectedLayout: Record<string, unknown>;
            journeyId?: string;
            app: unknown;
            theme: unknown;
            nodeIdMap?: Record<string, string>;
        }) {
            const messageNodes = (selectedLayout.messageGraph as Record<string, unknown[]>).nodes.filter(
                (node: unknown) => (node as Record<string, unknown>).nodeType === BRANCHING_NODES_TYPES.MESSAGE,
            );

            const layouts = getJourneyMessagesForJourneyLayout((selectedLayout.id as string), theme, {
                platform: (app as Record<string, unknown>).platform,
            });

            const guidePromises = (messageNodes as Array<Record<string, unknown>>).map(async (node) => {
                const layout = (layouts as Array<Record<string, unknown>>).find((l) => l.type === (node.messageData as Record<string, unknown>)?.layout);
                const isEmail = (layout as Record<string, unknown>)?.messageType === EMAIL;
                const emailTransactional = isEmailTransactional(layout);

                const { guide: guideFromLayout, isOrgResource, location } = await createGuideFromLayout(
                    layout,
                    {
                        app,
                        guideName: (node.messageData as Record<string, unknown>).name,
                        guideDescription: (node.messageData as Record<string, unknown>).description,
                        theme,
                        isEmail,
                        isEmailTransactional: emailTransactional,
                        isOrchestrate: true,
                        isOrchestrateTemplate: true,
                        maxPriority: useGuidesStore().maxPriority,
                        customLayoutTheme: useThemesStore().getThemeById((layout as Record<string, unknown>)?.themeId as string),
                        journeyId: this.getActiveJourney?.id,
                    },
                );
                const guide = await useGuidesStore().saveGuide({ guide: guideFromLayout, isOrgResource, location });

                return {
                    nodeId: node.id,
                    guideId: (guide as Record<string, unknown>).id,
                    messageNode: node,
                    layout,
                    isEmail,
                    ...(isEmail && { isEmailTransactional: get(guide, 'attributes.isEmailTransactional', false) }),
                };
            });

            const createdGuides = await Promise.all(guidePromises);

            const emailNodes: unknown[] = [];
            const regularNodes: unknown[] = [];

            createdGuides.forEach((item) => {
                if (item.isEmail) {
                    emailNodes.push(item);
                } else {
                    regularNodes.push(item);
                }
            });

            return { emailNodes, regularNodes };
        },
        async processEmailNodes ({ emailNodes, journeyId, app, theme }: {
            emailNodes: unknown[];
            journeyId: string;
            app: unknown;
            theme: unknown;
        }) {
            const emailPromises = (emailNodes as Array<Record<string, unknown>>).map(async ({ messageNode, layout, isEmailTransactional: transactional }) => {
                const { guide: guideFromLayout, isOrgResource, location } = await createGuideFromLayout(
                    layout,
                    {
                        app,
                        guideName: (messageNode as Record<string, unknown>).messageData && ((messageNode as Record<string, unknown>).messageData as Record<string, unknown>).name,
                        guideDescription: (messageNode as Record<string, unknown>).messageData && ((messageNode as Record<string, unknown>).messageData as Record<string, unknown>).description,
                        theme,
                        isEmail: true,
                        isEmailTransactional: transactional,
                        isOrchestrate: true,
                        isOrchestrateTemplate: true,
                        maxPriority: useGuidesStore().maxPriority,
                        customLayoutTheme: useThemesStore().getThemeById((layout as Record<string, unknown>)?.themeId as string),
                        journeyId: this.getActiveJourney?.id,
                    },
                );
                const guide = await useGuidesStore().saveGuide({ guide: guideFromLayout, isOrgResource, location });

                const email = await useEmailsStore().createOmnichannel({
                    name: ((messageNode as Record<string, unknown>).messageData as Record<string, unknown>)?.name as string,
                    description: ((messageNode as Record<string, unknown>).messageData as Record<string, unknown>)?.description as string,
                    appId: (app as Record<string, unknown>).id as string,
                    channelType: 'email',
                    guideId: (guide as Record<string, unknown>).id as string,
                    provider: AWS,
                    schedule: { sendPeriodically: true },
                    emailType: getEmailType(guide),
                });

                (messageNode as Record<string, unknown>).messageData = {
                    provider: AWS,
                    type: EMAIL,
                    id: (email as Record<string, unknown>).id,
                    layout,
                    durationInDays: ((messageNode as Record<string, unknown>).messageData as Record<string, unknown>)?.durationInDays,
                    emailData: {
                        name: ((messageNode as Record<string, unknown>).messageData as Record<string, unknown>)?.name,
                        guideId: (guide as Record<string, unknown>).id,
                        emailType: (email as Record<string, unknown>).emailType,
                        attributes: { journeyId },
                    },
                };
            });

            await Promise.all(emailPromises);
        },
        async createGraphNodesAndEdges ({ selectedLayout, journey, nodeIdMap }: {
            selectedLayout: Record<string, unknown>;
            journey: Journey;
            nodeIdMap: Record<string, string>;
        }) {
            const { useJourneysGraphStore } = await import('@/stores/journeys-graph.store');
            const graphStore = useJourneysGraphStore();

            const originalTemplateStartNodeId = Object.keys(nodeIdMap).find(
                (key) => nodeIdMap[key] === journey.messageGraph!.startNodeId,
            );
            const nodesToAdd = (selectedLayout.messageGraph as Record<string, unknown[]>).nodes.filter(
                (node: unknown) => (node as Record<string, unknown>).nodeType !== BRANCHING_NODES_TYPES.START,
            );

            await graphStore.setGraphNodes({ nodes: nodesToAdd });

            const updatedNodes = this.map[journey.id].messageGraph!.nodes;

            let journeyStartNode = updatedNodes.find((node) => node.nodeType === BRANCHING_NODES_TYPES.START);

            if (!journeyStartNode && journey.messageGraph!.startNodeId) {
                journeyStartNode = {
                    id: journey.messageGraph!.startNodeId,
                    nodeType: BRANCHING_NODES_TYPES.START,
                };
            }

            const templateStartNode = (selectedLayout.messageGraph as Record<string, unknown[]>).nodes.find(
                (node: unknown) => (node as Record<string, unknown>).nodeType === BRANCHING_NODES_TYPES.START,
            );

            if (templateStartNode && journeyStartNode) {
                nodeIdMap[(templateStartNode as Record<string, unknown>).id as string] = journeyStartNode.id;
            }

            if ((selectedLayout.messageGraph as Record<string, unknown>).startNodeId && journeyStartNode) {
                nodeIdMap[(selectedLayout.messageGraph as Record<string, unknown>).startNodeId as string] = journeyStartNode.id;
            }

            if (originalTemplateStartNodeId && journeyStartNode) {
                nodeIdMap[originalTemplateStartNodeId] = journeyStartNode.id;
            }

            (selectedLayout.messageGraph as Record<string, unknown[]>).edges.forEach((edge: unknown) => {
                const edgeRec = edge as Record<string, string>;
                if (edgeRec.from && !nodeIdMap[edgeRec.from] && journeyStartNode) {
                    const isStartNodeReference = !(selectedLayout.messageGraph as Record<string, unknown[]>).nodes.find(
                        (node: unknown) => (node as Record<string, unknown>).id === edgeRec.from,
                    );
                    if (isStartNodeReference) {
                        nodeIdMap[edgeRec.from] = journeyStartNode.id;
                    }
                }
            });

            const nonStartNodes = updatedNodes.filter((node) => node.nodeType !== BRANCHING_NODES_TYPES.START);
            (nodesToAdd as Array<Record<string, unknown>>).forEach((originalNode, index) => {
                nodeIdMap[originalNode.id as string] = nonStartNodes[index].id;
            });

            (selectedLayout.messageGraph as Record<string, unknown[]>).edges.forEach((edge: unknown) => {
                const edgeRec = edge as Record<string, string>;
                if (nodeIdMap[edgeRec.from]) edgeRec.from = nodeIdMap[edgeRec.from];
                if (nodeIdMap[edgeRec.to]) edgeRec.to = nodeIdMap[edgeRec.to];
            });

            await graphStore.setGraphEdges({ edges: (selectedLayout.messageGraph as Record<string, unknown[]>).edges });

            await this.loadOne({ id: journey.id, reFetch: true });
        },
        async loadIntegrationsByProvider ({
            provider,
            channel,
            emailType,
        }: {
            provider: string;
            channel: string;
            emailType?: string;
        }) {
            this.integrationError = { ...this.integrationError, [provider]: null };

            try {
                let list: unknown[] = [];

                switch (channel) {
                    case CHANNEL_TYPES.EMAIL:
                        ({ data: list } = await loadEmailsByProvider(provider, { emailType }));
                        break;
                    default:
                        break;
                }

                if (!this.integrationsByProvider[provider]) {
                    this.integrationsByProvider[provider] = {};
                }
                this.integrationsByProvider[provider][channel] = emailType
                    ? list.map((item) => ({ ...(item as Record<string, unknown>), emailType }))
                    : list;
            } catch {
                this.integrationError = {
                    ...this.integrationError,
                    [provider]: new Error(`Failed to load ${provider} ${channel}`),
                };
            }
        },
        setJourneyGraph ({ journeyId, messageGraph }: { journeyId?: string; messageGraph: unknown }) {
            const id = journeyId ?? this.currentJourneyId!;
            const journey = this.map[id];
            if (!journey) return;
            journey.messageGraph = messageGraph as Journey['messageGraph'];
        },
        async updateJourneyStatus ({ journeyId, status }: { journeyId: string; status: string }) {
            this.error = null;
            this.saving = true;

            try {
                const journey = this.map[journeyId];
                if (journey?.messageGraph?.nodes && journey?.messageGraph?.edges) {
                    const sanitized = await removeOrphanGraphElements(journeyId, journey.messageGraph);
                    if (!isEqual(sanitized.nodes, journey.messageGraph.nodes) || !isEqual(sanitized.edges, journey.messageGraph.edges)) {
                        this.setJourneyGraph({ journeyId, messageGraph: sanitized });
                    }
                }
                await updateJourneyStatusApi(journeyId, status);
                const j = this.map[journeyId];
                if (j) j.status = status;
            } catch (err) {
                console.error(err);
                this.error = ERROR_MESSAGES.JOURNEY_STATUS_UPDATE;
            }

            this.saving = false;
        },
        setIsLoadingMessages (isLoading: boolean) {
            this.loadingMessages = isLoading;
        },
        async updateMessageGraph ({ journeyId, messageGraph }: { journeyId?: string; messageGraph: unknown }) {
            this.error = null;
            this.saving = true;

            try {
                const updatedMessageGraph = await updateMessageGraphApi(journeyId || this.currentJourneyId!, messageGraph);
                const journey = this.map[(journeyId || this.currentJourneyId)!];
                if (journey) journey.messageGraph = updatedMessageGraph;

                return updatedMessageGraph;
            } catch (err) {
                this.error = ERROR_MESSAGES.JOURNEY_UPDATE;
                throw err;
            } finally {
                this.saving = false;
            }
        },
        async loadJourneyThrottlingPermission () {
            const settings = useSubscriptionStore().getSubscriptionJourneyThrottleSettings;
            if (!settings || !Object.keys(settings).length) {
                await useSubscriptionStore().ensureSubscriptionJourneyThrottleSettingsLoaded();
            }

            return this.hasJourneyThrottlingPermission;
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useJourneysStore, module.hot));
}
